import React, { useCallback } from 'react';

import { formatCurrency } from 'shared/helpers/utils';
import { ConsumerOrder } from 'src/components/embedded-account/account.types';

import {
  Row,
  TopLine,
  OrderInfo,
  TotalCost,
  BottomLine,
  Thumbnails,
  Thumbnail,
  MoreBadge,
  ReorderButton,
} from './order-row.styles';
import { formatOrderDate } from '../../../utils/format-order-date';
import { getUniqueOrderImages } from '../../../utils/get-unique-order-images';

type OrderRowProps = {
  order: ConsumerOrder;
  onReorder: () => void;
  reorderLoading: boolean;
  onViewOrder: () => void;
};

const MAX_THUMBNAILS = 3;

export function OrderRow({ order, onReorder, reorderLoading, onViewOrder }: OrderRowProps): JSX.Element {
  const { createdAt, orders: orderItems, totalCost } = order;
  const itemCount = orderItems.length;
  const formattedDate = createdAt ? formatOrderDate(createdAt) : '';
  const formattedTotal = formatCurrency(totalCost || '0');

  const uniqueImages = getUniqueOrderImages(orderItems);
  const visibleImages = uniqueImages.slice(0, MAX_THUMBNAILS);
  const moreCount = uniqueImages.length - MAX_THUMBNAILS;

  const handleReorderClick = (e: React.MouseEvent): void => {
    e.stopPropagation();
    onReorder();
  };

  const handleRowKeyDown = useCallback(
    (e: React.KeyboardEvent): void => {
      if (e.target !== e.currentTarget) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onViewOrder();
      }
    },
    [onViewOrder]
  );

  return (
    <Row
      onClick={onViewOrder}
      onKeyDown={handleRowKeyDown}
      tabIndex={0}
      aria-label='View order details'
      data-testid='order-row'
    >
      <TopLine>
        <OrderInfo>
          {formattedDate} &middot; {itemCount} {itemCount === 1 ? 'Item' : 'Items'}
        </OrderInfo>
        <TotalCost>{formattedTotal}</TotalCost>
      </TopLine>
      <BottomLine>
        <Thumbnails>
          {visibleImages.map((src) => (
            <Thumbnail key={src} src={src} htmlAttributes={{ alt: 'Product thumbnail' }} />
          ))}
          {moreCount > 0 && (
            <MoreBadge>
              <span>+{moreCount}</span>
              <span>more</span>
            </MoreBadge>
          )}
        </Thumbnails>
        <ReorderButton
          variant='secondary'
          onClick={handleReorderClick}
          disabled={reorderLoading}
          data-testid='order-row-reorder-button'
        >
          {reorderLoading ? 'Reordering...' : 'Reorder'}
        </ReorderButton>
      </BottomLine>
    </Row>
  );
}
